import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Eye, EyeOff, Cpu, Volume2, Mic, Brain, CheckSquare,
    Palette, Lock, CheckCircle, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { useDispatch } from "react-redux";

import { getLoginToken } from '@/redux/Auth/actions';
import { useAuth } from '@/config/AuthContext';

// CSS for animations
const styles = `
@keyframes stepReveal {
  0% {
    opacity: 0;
    transform: translateY(30px) scale(0.8) rotateX(20deg);
    filter: blur(4px);
  }
  50% {
    opacity: 0.7;
    transform: translateY(-5px) scale(1.05) rotateX(0deg);
    filter: blur(1px);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1) rotateX(0deg);
    filter: blur(0);
  }
}

@keyframes borderGlow {
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

.capability-card {
  position: relative;
  opacity: 0;
  animation: stepReveal 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}

.capability-card::before {
  content: '';
  position: absolute;
  inset: -1px;
  border-radius: 14px;
  padding: 1px;
  background: linear-gradient(
    135deg,
    rgba(139, 92, 246, 0.5) 0%,
    rgba(236, 72, 153, 0.5) 25%,
    rgba(59, 130, 246, 0.5) 50%,
    rgba(16, 185, 129, 0.5) 75%,
    rgba(139, 92, 246, 0.5) 100%
  );
  background-size: 300% 300%;
  animation: borderGlow 4s ease infinite;
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
  opacity: 0.6;
  transition: opacity 0.3s ease;
}

.capability-card:hover::before {
  opacity: 1;
}

.capability-card:nth-child(1) { animation-delay: 0s; }
.capability-card:nth-child(2) { animation-delay: 0.15s; }
.capability-card:nth-child(3) { animation-delay: 0.3s; }
.capability-card:nth-child(4) { animation-delay: 0.45s; }
.capability-card:nth-child(5) { animation-delay: 0.6s; }
.capability-card:nth-child(6) { animation-delay: 0.75s; }
`;


// Default Shipez logo — used everywhere unless a brand-specific logo
// is detected from the URL (and that brand's logo file actually exists).
const SHIPEZ_LOGO_URL =
    "https://customer-assets.emergentagent.com/job_c083c188-1797-4d1e-a073-4159fcae87e4/artifacts/z4ndtxat_shipez-logo-new.webp";

const LOCKER_FREIGHT_LOGO_URL =
    "https://api.shipez.ai/UploadedFile/CompanyProfile/Locker%20Freight/LockerFreight-logo.png";

// Plain utility — NOT a hook. Reads the current URL and decides if this is
// the Locker Freight branded login route.
function isLockerFreightPath() {
    const pathname = window.location?.pathname || "";
    return pathname === "/locker-freight/login" || pathname.includes("locker-freight");
}


const Login = () => {
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [loading, setLoading] = useState(false);
    const [focusedField, setFocusedField] = useState(null);
    const dispatch = useDispatch();
    const { setIsAuthenticated } = useAuth();

    // === Dynamic brand logo logic ===
    // Decide synchronously based on the current URL. We let the <img> tag
    // load the URL directly — if the brand logo is unreachable, the <img>'s
    // onError handler swaps in the Shipez fallback. We can't use fetch() to
    // pre-check the URL because the brand CDN is cross-origin and the HEAD
    // request gets blocked by CORS, which would make us always fall back.
    const onLockerFreightRoute = isLockerFreightPath();
    const [isLockerFreight] = useState(onLockerFreightRoute);
    const [customLogo, setCustomLogo] = useState(
        onLockerFreightRoute ? LOCKER_FREIGHT_LOGO_URL : SHIPEZ_LOGO_URL
    );

    // Inject styles on mount
    useEffect(() => {
        const styleSheet = document.createElement("style");
        styleSheet.innerText = styles;
        styleSheet.id = "login-animations";
        document.head.appendChild(styleSheet);
        return () => {
            const existingStyle = document.getElementById("login-animations");
            if (existingStyle) {
                existingStyle.remove();
            }
        };
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const payload = JSON.stringify({
            "username": formData.email,
            "password": formData.password
        });
        try {
            const authToken = await dispatch(getLoginToken(payload));
            if (authToken) {
                toast.success('Login successful!');
                setIsAuthenticated(true);
                navigate('/dashboard');
            }
        } catch (error) {
            console.log(error);
            const errorMessage = typeof error === 'string' ? error : (error?.message || error?.response?.data?.detail || 'Login failed');
            toast.error(errorMessage);
        }
        setLoading(false);
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    // AI Capabilities data
    const capabilities = [
        { icon: Eye, title: 'See', subtitle: 'Visual Analysis' },
        { icon: Volume2, title: 'Speak', subtitle: 'Voice Output' },
        { icon: Mic, title: 'Hear', subtitle: 'Voice Input' },
        { icon: Brain, title: 'Think', subtitle: 'AI Analysis' },
        { icon: CheckSquare, title: 'Plan', subtitle: 'Smart Planning' },
        { icon: Palette, title: 'Create', subtitle: 'Content Gen' }
    ];

    return (
        <div className="flex flex-col lg:flex-row min-h-screen w-full" style={{ backgroundColor: '#001441' }}>
            {/* Left Section - Hidden on mobile, 2/3 on desktop */}
            <div className="hidden lg:flex relative lg:w-2/3 overflow-hidden flex-col justify-between p-6 lg:p-12">
                {/* Video Background */}
                <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="absolute top-0 left-0 w-full h-full object-cover z-0 pointer-events-none block"
                >
                    <source src="https://customer-assets.emergentagent.com/job_c083c188-1797-4d1e-a073-4159fcae87e4/artifacts/openlpzu_hero-video-new%201.mp4" type="video/mp4" />
                </video>

                {/* Dark Overlay */}
                <div
                    className="absolute inset-0 z-[1] pointer-events-none"
                    style={{ background: 'rgba(0, 20, 65, 0.3)' }}
                />

                {/* Gradient Overlay */}
                <div
                    className="absolute inset-0 z-[2] pointer-events-none"
                    style={{
                        background: 'radial-gradient(circle at 20% 30%, rgb(0 3 7 / 70%) 0%, #000000e3 50%), radial-gradient(circle at 80% 70%, rgba(160, 224, 13, 0.1) 0%, transparent 50%)'
                    }}
                />

                {/* Content Layer */}
                <div className="relative z-[3] flex flex-col h-full">
                    {/* Logo (dynamic — Shipez by default, Locker Freight on
                        /locker-freight/login if that brand's logo is reachable) */}
                    <div className="mb-8">
                        <img
                            src={customLogo}
                            alt={isLockerFreight ? "Locker Freight Logo" : "Shipez Logo"}
                            className="h-[72px] w-auto"
                            onError={() => {
                                // If the brand logo can't load (404, network,
                                // wrong path, etc.) drop back to the Shipez
                                // logo so we never display a broken image.
                                if (customLogo !== SHIPEZ_LOGO_URL) {
                                    setCustomLogo(SHIPEZ_LOGO_URL);
                                }
                            }}
                        />
                    </div>

                    {/* Welcome Section */}
                    <div className="mb-12">
                        <h1
                            className="text-3xl lg:text-5xl font-normal leading-tight mb-2"
                            style={{
                                backgroundImage: 'linear-gradient(to right, rgb(78, 131, 255) 0%, rgb(5, 192, 0) 30%, rgb(95, 168, 163) 50%, rgb(170, 217, 0) 70%, rgb(170, 217, 0) 100%)',
                                WebkitBackgroundClip: 'text',
                                backgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                minHeight: '1.2em'
                            }}
                        >
                            Experience The Revolution in Digital Freight Forwarding
                        </h1>
                        <p className="text-base lg:text-xl text-white/80 font-normal">
                            Centralized AI Powered Shipping Platform
                        </p>
                    </div>

                    {/* AI Capabilities Section */}
                    <div className="flex-1 flex flex-col mt-8 lg:mt-20">
                        {/* Header */}
                        <div className="flex items-center gap-3 mb-4">
                            <Cpu className="w-6 h-6 flex-shrink-0" style={{ color: '#c8ff00' }} />
                            <h2 className="text-xl lg:text-2xl font-semibold text-white italic">AI Capabilities</h2>
                        </div>

                        {/* Description */}
                        <p className="text-sm lg:text-base text-white/80 mb-8 lg:mb-16 leading-relaxed italic">
                            Our TMS platform is fully AI-enabled with advanced capabilities.
                        </p>

                        {/* Cards Grid */}
                        <div className="grid grid-cols-3 lg:grid-cols-6 gap-3 lg:gap-6 mb-8 max-w-[95%]">
                            {capabilities.map((cap, index) => (
                                <div
                                    key={cap.title}
                                    className="capability-card group flex flex-col items-center text-center p-4 lg:p-6 rounded-xl cursor-pointer transition-all duration-300 hover:-translate-y-1.5 hover:scale-105"
                                    style={{
                                        background: 'rgba(0, 20, 65, 0.6)',
                                        backdropFilter: 'blur(20px)',
                                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                                        minHeight: '120px'
                                    }}
                                >
                                    <div className="flex items-center justify-center mb-3 lg:mb-4 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-[5deg]">
                                        <cap.icon className="w-6 h-6 lg:w-7 lg:h-7 text-white" strokeWidth={1.5} />
                                    </div>
                                    <span className="text-sm lg:text-lg font-semibold text-white mb-1 lg:mb-2 transition-colors duration-300 group-hover:text-[#c8ff00]">
                                        {cap.title}
                                    </span>
                                    <span className="text-xs lg:text-sm text-white/60 font-normal">
                                        {cap.subtitle}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Compliance Badges */}
                    <div className="flex gap-4 justify-center mt-auto pt-8">
                        <div
                            className="flex items-center gap-2 px-3 lg:px-4 py-2 rounded-lg text-xs lg:text-sm text-white/90"
                            style={{
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                backdropFilter: 'blur(10px)'
                            }}
                        >
                            <Lock className="w-4 h-4" style={{ color: '#ffb347' }} />
                            ISO 27001 Certified
                        </div>
                        <div
                            className="flex items-center gap-2 px-3 lg:px-4 py-2 rounded-lg text-xs lg:text-sm text-white/90"
                            style={{
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                backdropFilter: 'blur(10px)'
                            }}
                        >
                            <CheckCircle className="w-4 h-4" style={{ color: '#4ade80' }} />
                            GDPR Ready
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Section - Full width on mobile, 1/3 on desktop */}
            <div className="w-full lg:w-1/3 min-h-screen lg:min-h-0 flex items-center justify-center p-4 sm:p-6 lg:p-12 relative overflow-hidden" style={{ backgroundColor: '#0a0f0a' }}>
                {/* Mobile Logo - Only shown on mobile (uses the same
                    dynamic brand logo as the desktop view) */}
                <div className="absolute top-4 left-4 lg:hidden">
                    <img
                        src={customLogo}
                        alt={isLockerFreight ? "Locker Freight Logo" : "Shipez Logo"}
                        className="h-10 w-auto"
                        onError={() => {
                            if (customLogo !== SHIPEZ_LOGO_URL) {
                                setCustomLogo(SHIPEZ_LOGO_URL);
                            }
                        }}
                    />
                </div>

                {/* Login Form Container */}
                <div className="w-full max-w-[480px] p-6 sm:p-8 lg:p-12 rounded-3xl relative z-[1]">
                    {/* Form Header */}
                    <div className="mb-6 sm:mb-10 text-center">
                        <h2
                            className="text-3xl sm:text-4xl font-bold mb-2 sm:mb-3 italic"
                            style={{
                                backgroundImage: 'linear-gradient(to right, #22c55e, #84cc16)',
                                WebkitBackgroundClip: 'text',
                                backgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                letterSpacing: '-0.02em'
                            }}
                        >
                            Sign In
                        </h2>
                        <p className="text-sm sm:text-base leading-relaxed" style={{ color: '#9ca3af' }}>
                            Welcome back! Please enter your details to continue.
                        </p>
                    </div>

                    {/* Login Form */}
                    <form onSubmit={handleSubmit} className="flex flex-col gap-5 sm:gap-7">
                        {/* Email/Username Field */}
                        <div className="flex flex-col gap-2">
                            <label
                                className="text-sm font-semibold mb-1 sm:mb-2 transition-colors duration-200"
                                style={{ color: '#e5e7eb', letterSpacing: '0.01em' }}
                            >
                                Email Address
                            </label>
                            <div className="relative flex items-center">
                                <input
                                    type="text"
                                    value={formData.email}
                                    name='email'
                                    onChange={(e) => handleChange(e)}
                                    onFocus={() => setFocusedField('email')}
                                    onBlur={() => setFocusedField(null)}
                                    placeholder="Enter your username"
                                    className="w-full py-3 sm:py-[18px] px-4 rounded-xl text-sm sm:text-[15px] font-medium transition-all duration-300 focus:outline-none focus:border-[#84cc16] placeholder-gray-500"
                                    style={{
                                        border: '1.5px solid #4d5c2e',
                                        background: 'transparent',
                                        color: '#e5e7eb'
                                    }}
                                    required
                                />
                            </div>
                        </div>

                        {/* Password Field */}
                        <div className="flex flex-col gap-2">
                            <label
                                className="text-sm font-semibold mb-1 sm:mb-2 transition-colors duration-200"
                                style={{ color: '#e5e7eb', letterSpacing: '0.01em' }}
                            >
                                Password
                            </label>
                            <div className="relative flex items-center">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    value={formData.password}
                                    name='password'
                                    onChange={(e) => handleChange(e)}
                                    onFocus={() => setFocusedField('password')}
                                    onBlur={() => setFocusedField(null)}
                                    placeholder="Enter your password"
                                    className="w-full py-3 sm:py-[18px] px-4 pr-12 rounded-xl text-sm sm:text-[15px] font-medium transition-all duration-300 focus:outline-none focus:border-[#84cc16] placeholder-gray-500"
                                    style={{
                                        border: '1.5px solid #4d5c2e',
                                        background: 'transparent',
                                        color: '#e5e7eb'
                                    }}
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3.5 top-1/2 -translate-y-1/2 p-2 rounded-lg flex items-center justify-center transition-all duration-300 z-[2] hover:scale-110"
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#6b7280'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.color = '#84cc16';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.color = '#6b7280';
                                    }}
                                >
                                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                        </div>

                        {/* Form Options */}
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0 text-sm -mt-1 sm:-mt-2">
                            <label
                                className="flex items-center gap-2.5 cursor-pointer font-medium transition-colors duration-200 hover:text-white"
                                style={{ color: '#9ca3af' }}
                            >
                                <input
                                    name='rememberMe'
                                    type="checkbox"
                                    checked={formData?.rememberMe}
                                    onChange={(e) => handleChange(e)}
                                    className="w-[18px] h-[18px] rounded bg-transparent border-gray-600"
                                    style={{ accentColor: '#84cc16' }}
                                />
                                Remember me
                            </label>
                            <a
                                href="#"
                                className="font-semibold no-underline py-1 px-2 rounded-md -my-1 -mx-2 transition-all duration-200"
                                style={{ color: '#84cc16' }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.color = '#a3e635';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.color = '#84cc16';
                                }}
                            >
                                Forgot password?
                            </a>
                        </div>

                        {/* Sign In Button */}
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full py-3 sm:py-[18px] text-slate-900 font-semibold text-sm sm:text-base rounded-full border-none cursor-pointer relative overflow-hidden mt-2 sm:mt-3 transition-all duration-300 hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-70 disabled:cursor-not-allowed group"
                            style={{
                                background: 'linear-gradient(to right, #0ea5e9, #22c55e, #84cc16)',
                                letterSpacing: '0.01em'
                            }}
                            onMouseEnter={(e) => {
                                if (!loading) {
                                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(132, 204, 22, 0.3)';
                                }
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.boxShadow = 'none';
                            }}
                        >
                            {/* Shine Effect */}
                            <span
                                className="absolute top-0 left-[-100%] w-full h-full transition-all duration-500 group-hover:left-full"
                                style={{
                                    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)'
                                }}
                            />
                            {loading ? (
                                <span className="flex items-center justify-center gap-2">
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    Signing in...
                                </span>
                            ) : (
                                'Sign In'
                            )}
                        </button>
                    </form>

                    {/* Sign Up Link */}
                    <div
                        className="text-center text-sm sm:text-[15px] mt-4 sm:mt-6 pt-4 sm:pt-6"
                        style={{ color: '#9ca3af', borderTop: '1px solid #374151' }}
                    >
                        Don't have an account?
                        <a
                            href="/signup"
                            className="font-semibold no-underline ml-2 py-1 px-2 rounded-md inline-block transition-all duration-200"
                            style={{ color: '#84cc16' }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.color = '#a3e635';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.color = '#84cc16';
                            }}
                        >
                            Sign up
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
